"use client";

import React, { useState, useEffect } from "react";
import FlyingBees from "./FlyingBees";

interface CameraComponentProps {
    title: string;
    src: string;
}

function CameraComponent({ title, src }: CameraComponentProps) {
    const [consentGiven, setConsentGiven] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("reload") !== null) {
            setConsentGiven(true);
            return;
        }

        const consentCookie = document.cookie.split("; ").find((row) => row.startsWith("youtube-consent="));

        if (consentCookie) {
            const consentValue = consentCookie.split("=")[1];
            if (consentValue === "granted") {
                setConsentGiven(true);
            }
        }
    }, []);

    const handleConsent = () => {
        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        document.cookie = `youtube-consent=granted; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict`;

        setConsentGiven(true);
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 flex flex-col items-center">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">{title}</h3>
            <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                {!consentGiven ? (
                    <div className="text-center max-w-xs sm:max-w-sm p-4">
                        <svg
                            className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                        </svg>
                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">YouTube-Video laden</h4>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1 leading-relaxed">
                            Durch das Laden dieses Videos akzeptieren Sie die Datenschutzerklärung von YouTube/Google.
                        </p>
                        <p className="text-xs text-gray-500 mb-1">
                            <a href="/privacy" className="text-amber-600 hover:text-amber-700 underline">
                                Datenschutzerklärung
                            </a>
                        </p>
                        <button
                            type="button"
                            onClick={handleConsent}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm w-full"
                        >
                            Video laden
                        </button>
                    </div>
                ) : (
                    <iframe
                        src={src}
                        title={title}
                        allow="autoplay; encrypted-media"
                        allowFullScreen={true}
                        className="w-full h-full"
                    />
                )}
            </div>
        </div>
    );
}

export default function CameraSection() {
    return (
        <section className="camera-section relative py-16 px-6" style={{ backgroundColor: "#fefdf8" }}>
            <div className="max-w-6xl mx-auto relative z-20">
                {/* Section Header */}
                <div className="text-center mb-12">
                    <h2 className="text-5xl font-bold text-gray-900 mb-4">Live-Kamera</h2>
                    <p className="text-lg text-amber-600 font-medium">Beobachte unsere Bienen bei der Arbeit</p>
                </div>

                {/* Camera Feeds */}
                <div className="grid lg:grid-cols-2 gap-12 items-start">
                    {/* Camera 1 */}
                    <CameraComponent
                        title="Bienenstock 1"
                        src={`https://www.youtube.com/embed/${process.env.NEXT_PUBLIC_STREAM1_ID}?controls=0&autoplay=1`}
                    />

                    {/* Camera 2 */}
                    <CameraComponent
                        title="Bienenstock 2 + 3"
                        src={`https://www.youtube.com/embed/${process.env.NEXT_PUBLIC_STREAM2_ID}?controls=0&autoplay=1`}
                    />
                </div>
            </div>

            <FlyingBees
                bees={[
                    { top: "25%", left: "12%", width: 90 },
                    { top: "50%", left: "88%", width: 80 },
                    { top: "75%", left: "45%", width: 85 },
                ]}
            />
        </section>
    );
}
